if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.productComponent = this.productInfo?.querySelector('product-component');
        this.sectionId = this.dataset.sectionId;

        this.select = this.querySelector('.sticky-product-bar__select');
        this.button = this.querySelector('.sticky-product-bar__button');
        this.buttonLabel = this.querySelector('.sticky-product-bar__button-label');
        this.price = this.querySelector('.sticky-product-bar__price');

        this.originalButton = this.productInfo?.querySelector(
          `#ProductSubmitButton-${this.sectionId}`
        );

        if (
          !this.productInfo ||
          !this.productComponent ||
          !this.originalButton ||
          !this.button
        ) {
          return;
        }

        this.footerIsIntersecting = false;
        this.visibilityUpdatePending = false;

        this.addDecorativeBorder();

        this.onSelectChange = this.handleSelectChange.bind(this);

        this.onButtonClick = () => {
          this.originalButton?.click();
        };

        this.onScroll = this.requestVisibilityUpdate.bind(this);

        this.onResize = () => {
          this.setVisibilityThreshold();
          this.requestVisibilityUpdate();
        };

        this.select?.addEventListener('change', this.onSelectChange);
        this.button.addEventListener('click', this.onButtonClick);

        window.addEventListener('scroll', this.onScroll, {
          passive: true,
        });

        window.addEventListener('resize', this.onResize);

        /*
         * Calculate when sticky bar should appear.
         */
        this.setVisibilityThreshold();
        this.updateVisibility();

        /*
         * Recalculate if product-component height changes.
         */
        this.productComponentResizeObserver = new ResizeObserver(() => {
          this.setVisibilityThreshold();
          this.requestVisibilityUpdate();
        });

        this.productComponentResizeObserver.observe(this.productComponent);

        /*
         * Hide sticky bar when footer is visible.
         */
        const footer = document.querySelector('footer.footer');

        if (footer) {
          this.footerObserver = new IntersectionObserver(
            ([entry]) => {
              this.footerIsIntersecting = entry.isIntersecting;
              this.requestVisibilityUpdate();
            },
            {
              threshold: 0,
            }
          );

          this.footerObserver.observe(footer);
        }

        /*
         * Shopify variant change listener.
         */
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
        }
      }

      /*
       * Decorative border
       */
      addDecorativeBorder() {
        const borderContainer = this.querySelector(
          '.sticky-product-bar__border'
        );

        const headerBorder = document.querySelector(
          '.icon-union-border-wrapper-header-bar svg'
        );

        if (!borderContainer || !headerBorder) return;

        const border = headerBorder.cloneNode(true);

        const filter = border.querySelector('filter');
        const filteredGroup = border.querySelector('g[filter]');

        if (filter && filteredGroup) {
          const filterId = `sticky-product-bar-shadow-${this.sectionId}`;

          filter.id = filterId;

          filteredGroup.setAttribute(
            'filter',
            `url(#${filterId})`
          );
        }

        border.removeAttribute('width');
        border.removeAttribute('height');

        border.setAttribute(
          'preserveAspectRatio',
          'none'
        );

        borderContainer.replaceChildren(border);
      }

      /*
       * Sticky bar appears when the bottom of
       * product-component reaches the bottom
       * of the viewport.
       */
      setVisibilityThreshold() {
        const productBounds =
          this.productComponent.getBoundingClientRect();

        const productBottom =
          productBounds.bottom + window.scrollY;

        this.visibilityThreshold =
          productBottom - window.innerHeight;
      }

      /*
       * Throttle scroll updates using requestAnimationFrame.
       */
      requestVisibilityUpdate() {
        if (this.visibilityUpdatePending) return;

        this.visibilityUpdatePending = true;

        requestAnimationFrame(() => {
          this.updateVisibility();
        });
      }

      /*
       * Show / hide sticky bar
       */
      updateVisibility() {
        const passedProduct =
          window.scrollY >= this.visibilityThreshold;

        const shouldShow =
          passedProduct &&
          !this.footerIsIntersecting;

        this.classList.toggle(
          'is-visible',
          shouldShow
        );

        this.setAttribute(
          'aria-hidden',
          String(!shouldShow)
        );

        this.toggleAttribute(
          'inert',
          !shouldShow
        );

        this.visibilityUpdatePending = false;
      }

      /*
       * Sticky variant selector
       */
      handleSelectChange(event) {
        const optionValue = event.target.value;

        if (!optionValue) return;

        const firstOptionGroup =
          this.productInfo.querySelector(
            'variant-selects .product-form__input'
          );

        if (!firstOptionGroup) return;

        const originalSelect =
          firstOptionGroup.querySelector('select');

        if (originalSelect) {
          originalSelect.value = optionValue;

          originalSelect.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        } else {
          const originalRadio = Array.from(
            firstOptionGroup.querySelectorAll(
              'input[type="radio"]'
            )
          ).find(
            (input) =>
              input.value === optionValue
          );

          originalRadio?.click();
        }

        event.target.selectedIndex = 0;
      }

      /*
       * Update sticky bar when variant changes
       */
      handleVariantChange({ data }) {
        if (
          String(data.sectionId) !==
          String(this.sectionId)
        ) {
          return;
        }

        const { html, variant } = data;

        const sourceButton =
          html.getElementById(
            `ProductSubmitButton-${this.sectionId}`
          );

        const sourcePrice =
          html.querySelector(
            `#price-${this.sectionId} .price`
          );

        const isAvailable =
          Boolean(variant?.available) &&
          !sourceButton?.hasAttribute('disabled');

        /*
         * Update button
         */
        this.button.disabled = !isAvailable;

        if (this.buttonLabel) {
          this.buttonLabel.textContent =
            isAvailable
              ? `ADD ${variant.options[0].toUpperCase()} TO BASKET`
              : window.variantStrings?.soldOut || 'SOLD OUT';
        }

        /*
         * Update price
         */
        if (sourcePrice && this.price) {
          this.price.innerHTML =
            sourcePrice.outerHTML;
        }

        /*
         * Refresh original add-to-cart button reference
         */
        this.originalButton =
          this.productInfo.querySelector(
            `#ProductSubmitButton-${this.sectionId}`
          );
      }

      /*
       * Cleanup
       */
      disconnectedCallback() {
        this.select?.removeEventListener(
          'change',
          this.onSelectChange
        );

        this.button?.removeEventListener(
          'click',
          this.onButtonClick
        );

        window.removeEventListener(
          'scroll',
          this.onScroll
        );

        window.removeEventListener(
          'resize',
          this.onResize
        );

        this.productComponentResizeObserver?.disconnect();

        this.footerObserver?.disconnect();

        this.variantChangeUnsubscriber?.();
      }
    }
  );
}
